/**
 * Inline SVG illustrations for empty states / headings.
 *
 * Intimate/editorial identity: fine strokes, a palette derived from theme tokens
 * (currentColor + accent) so they respect any accent. Decorative by default
 * (`aria-hidden`); anyone using them must provide the accessible text.
 *
 * They do not depend on fixed colors: they inherit `text-*` from the container
 * and use `text-primary`/`text-muted-foreground` via utility classes.
 */
import { type Component, type ComponentProps, type JSX, splitProps } from "solid-js";
import { cn } from "@journal/shared";

type IllustrationProps = ComponentProps<"svg">;

/**
 * Base SVG canvas shared by all illustrations. It preserves the reactivity of
 * `class` (used directly in the wrapper JSX) and applies the common attributes
 * (viewBox, decorative by default).
 */
function IllustrationSvg(props: IllustrationProps & { children: JSX.Element }) {
  const [local, rest] = splitProps(props, ["class", "children"]);
  return (
    <svg
      class={cn("h-32 w-32", local.class)}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...rest}
    >
      {local.children}
    </svg>
  );
}

/** Open notebook with a pen — main writing empty state. */
export const OpenJournalIllustration: Component<IllustrationProps> = (props) => (
  <IllustrationSvg {...props}>
    {/* Halo suave del acento */}
    <circle cx="60" cy="60" r="46" class="fill-primary/8" />
    {/* Pages */}
    <path
      d="M24 40c10-5 22-5 36 1 14-6 26-6 36-1v44c-10-5-22-5-36 1-14-6-26-6-36-1V40Z"
      class="fill-card stroke-primary/40"
      stroke-width="2.5"
      stroke-linejoin="round"
    />
    {/* Lomo central */}
    <path d="M60 41v45" class="stroke-primary/30" stroke-width="2" stroke-linecap="round" />
    {/* Renglones */}
    <path
      d="M32 52h18M32 60h18M32 68h14M70 52h18M70 60h18M70 68h14"
      class="stroke-muted-foreground/40"
      stroke-width="2"
      stroke-linecap="round"
    />
    {/* Pluma */}
    <path
      d="M86 30c-8 4-16 12-20 22l-3 8 8-3c10-4 18-12 22-20l-7-7Z"
      class="fill-primary/15 stroke-primary"
      stroke-width="2.5"
      stroke-linejoin="round"
    />
    <path d="M63 60l6-6" class="stroke-primary" stroke-width="2.5" stroke-linecap="round" />
  </IllustrationSvg>
);

/** Star with sparkles — empty favorites. */
export const StarredIllustration: Component<IllustrationProps> = (props) => (
  <IllustrationSvg {...props}>
    <circle cx="60" cy="60" r="46" class="fill-primary/8" />
    <path
      d="M60 34l8.5 17.2 19 2.8-13.7 13.4 3.2 18.9L60 77.4 42.9 86.3l3.2-18.9L32.4 54l19-2.8L60 34Z"
      class="fill-primary/15 stroke-primary"
      stroke-width="2.5"
      stroke-linejoin="round"
    />
    <path
      d="M30 34l2-6 2 6 6 2-6 2-2 6-2-6-6-2 6-2ZM86 84l1.5-4.5 1.5 4.5 4.5 1.5-4.5 1.5L88 96l-1.5-4.5-4.5-1.5 4.5-1.5Z"
      class="fill-primary/60"
    />
  </IllustrationSvg>
);

/** Tag with a thread — empty tags. */
export const TagIllustration: Component<IllustrationProps> = (props) => (
  <IllustrationSvg {...props}>
    <circle cx="60" cy="60" r="46" class="fill-primary/8" />
    <path
      d="M40 34h22a6 6 0 0 1 4.2 1.8l24 24a6 6 0 0 1 0 8.4L74 84a6 6 0 0 1-8.4 0l-24-24A6 6 0 0 1 40 56V34Z"
      class="fill-card stroke-primary/45"
      stroke-width="2.5"
      stroke-linejoin="round"
    />
    <circle cx="52" cy="46" r="4.5" class="fill-primary" />
  </IllustrationSvg>
);

/** Magnifying glass over a page — empty search / no results. */
export const SearchIllustration: Component<IllustrationProps> = (props) => (
  <IllustrationSvg {...props}>
    <circle cx="60" cy="60" r="46" class="fill-primary/8" />
    <rect
      x="34"
      y="32"
      width="40"
      height="52"
      rx="6"
      class="fill-card stroke-muted-foreground/35"
      stroke-width="2.5"
    />
    <path
      d="M42 44h24M42 52h24M42 60h16"
      class="stroke-muted-foreground/35"
      stroke-width="2"
      stroke-linecap="round"
    />
    <circle cx="70" cy="68" r="14" class="fill-card stroke-primary" stroke-width="3" />
    <path d="M80 78l9 9" class="stroke-primary" stroke-width="3.5" stroke-linecap="round" />
  </IllustrationSvg>
);

/**
 * Notebook with a folded/torn page — error fallback (ErrorBoundary).
 * Theme-aware: uses the container's `text-destructive` + accents derived from
 * tokens, never fixed colors. Decorative (aria-hidden); the text is provided
 * by the fallback that uses it.
 */
export const BrokenJournalIllustration: Component<IllustrationProps> = (props) => (
  <IllustrationSvg {...props}>
    {/* Halo suave de alerta */}
    <circle cx="60" cy="60" r="46" class="fill-destructive/8" />
    {/* Cuaderno */}
    <rect
      x="32"
      y="30"
      width="44"
      height="58"
      rx="6"
      class="fill-card stroke-destructive/40"
      stroke-width="2.5"
    />
    {/* Esquina doblada */}
    <path
      d="M76 30v14h14"
      class="fill-destructive/10 stroke-destructive/40"
      stroke-width="2.5"
      stroke-linejoin="round"
    />
    {/* Renglones interrumpidos */}
    <path
      d="M40 46h22M40 54h18M40 62h12"
      class="stroke-muted-foreground/35"
      stroke-width="2"
      stroke-linecap="round"
    />
    {/* Grieta / zig-zag */}
    <path
      d="M48 70l8 6-6 6 8 6"
      class="stroke-destructive"
      stroke-width="3"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    {/* Destellos */}
    <path d="M86 64l1.5 4 4 1.5-4 1.5-1.5 4-1.5-4-4-1.5 4-1.5 1.5-4Z" class="fill-destructive/60" />
  </IllustrationSvg>
);

/** Mood chart — analytics without enough data. */
export const MoodChartIllustration: Component<IllustrationProps> = (props) => (
  <IllustrationSvg {...props}>
    <circle cx="60" cy="60" r="46" class="fill-primary/8" />
    <path
      d="M32 80V44M32 80h56"
      class="stroke-muted-foreground/40"
      stroke-width="2.5"
      stroke-linecap="round"
    />
    <path
      d="M36 72c8-2 12-14 20-14s10 10 18 6 8-18 14-22"
      class="stroke-primary"
      stroke-width="3"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <circle cx="56" cy="58" r="3.5" class="fill-primary" />
    <circle cx="74" cy="64" r="3.5" class="fill-primary" />
  </IllustrationSvg>
);
